import { BadgeCheck, ListTodo, Image, FileText, Search, Moon, CornerDownLeft } from "lucide-react";
import { Reveal, Chapter, SectionHeading } from "./Reveal";

const FeatureBlock = ({ id, chapter, label, headline, copy, children, flip = false, dark = false }) => (
  <section id={id} className={`${dark ? "bg-diem-ink" : "bg-white"} py-24 sm:py-32`} data-testid={`${id}-section`}>
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Chapter index={chapter} label={label} dark={dark} />
      <div className={`mt-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center`}>
        <div className={flip ? "lg:order-2" : ""}>
          <Reveal delay={0.1}>
            <SectionHeading dark={dark}>{headline}</SectionHeading>
          </Reveal>
          <Reveal delay={0.2}>
            <div className={`mt-7 text-lg leading-relaxed max-w-lg ${dark ? "text-white/60" : "text-diem-slate"}`}>{copy}</div>
          </Reveal>
        </div>
        <Reveal delay={0.2} className={flip ? "lg:order-1" : ""}>
          {children}
        </Reveal>
      </div>
    </div>
  </section>
);

const Card = ({ children, dark = false, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-3xl p-6 sm:p-8 ${
      dark
        ? "bg-white/[0.04] border border-white/10"
        : "bg-white border border-diem-line shadow-[0_24px_60px_-16px_rgba(15,23,42,0.15)]"
    }`}
  >
    {children}
  </div>
);

export const Approvals = () => (
  <FeatureBlock
    id="approvals"
    chapter="06"
    label="Approvals"
    headline={<>"Yes" should mean something.</>}
    copy={
      <>
        <p>Decisions disappear inside conversations. Deyem keeps them.</p>
        <p className="mt-4 font-medium text-diem-ink">Know what was agreed, who agreed it and when.</p>
      </>
    }
  >
    <Card testid="approvals-demo">
      <div className="max-w-[90%] rounded-2xl rounded-bl-md bg-diem-mist px-4 py-3 text-[14px] text-diem-ink">
        "Final quote — anthracite frames, £3,840 fitted. Happy to proceed?"
      </div>
      <div className="mt-4 rounded-2xl border-2 border-diem-ok/40 bg-diem-ok/[0.06] p-5">
        <div className="flex items-center gap-2.5">
          <BadgeCheck className="w-6 h-6 text-diem-ok" />
          <span className="font-display text-xl font-semibold tracking-tight text-diem-ok">APPROVED</span>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <img className="w-9 h-9 rounded-full object-cover" alt="Sarah M." loading="lazy"
            src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGhlYWRzaG90fGVufDB8fHx8MTc4Nzc2OTIzOXww&ixlib=rb-4.1.0&q=85&w=96" />
          <div>
            <p className="text-sm font-bold text-diem-ink">Sarah M. · Project Owner</p>
            <p className="text-xs text-diem-muted">Tuesday 14:32</p>
          </div>
        </div>
        <button className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold text-diem-blue hover:gap-2.5 transition-[gap] duration-300" data-testid="approval-context-link">
          <CornerDownLeft className="w-3.5 h-3.5" /> View original conversation
        </button>
      </div>
    </Card>
  </FeatureBlock>
);

export const Tasks = () => (
  <FeatureBlock
    id="tasks"
    chapter="07"
    label="Tasks & deadlines"
    headline={<>Turn conversation into action.</>}
    copy={
      <>
        <p>A normal message becomes a task — still connected to the conversation it came from.</p>
        <p className="mt-4 font-medium text-diem-ink">No separate project-management system required.</p>
      </>
    }
    flip
  >
    <Card testid="tasks-demo">
      <div className="max-w-[90%] ml-auto rounded-2xl rounded-br-md bg-gradient-to-r from-[#1E3A8A] to-[#38BDF8] px-4 py-3 text-[14px] text-white">
        "Can you book the electrician for Tuesday?"
      </div>
      <div className="mt-4 rounded-2xl border border-diem-line bg-diem-paper p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-diem-blue/10 flex items-center justify-center">
              <ListTodo className="w-5 h-5 text-diem-blue" />
            </span>
            <div>
              <p className="font-display text-lg font-semibold tracking-tight text-diem-ink">Book electrician</p>
              <p className="text-xs text-diem-muted font-medium">From a message · Sarah → James</p>
            </div>
          </div>
          <span className="rounded-full bg-diem-warn/10 text-diem-warn text-[11px] font-bold px-3 py-1">Open</span>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className="rounded-full bg-diem-blue text-white text-[11px] font-bold px-3 py-1.5">Tuesday</span>
          <span className="rounded-full bg-white border border-diem-line text-[11px] font-bold text-diem-slate px-3 py-1.5">Oakfield Drive</span>
        </div>
      </div>
    </Card>
  </FeatureBlock>
);

export const MediaSearch = () => (
  <section id="photos" className="bg-diem-paper py-24 sm:py-32" data-testid="media-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Chapter index="08" label="Photos, documents & search" />
      <div className="mt-8 max-w-3xl">
        <Reveal delay={0.1}>
          <SectionHeading>Stop scrolling for that photo.</SectionHeading>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-7 text-lg leading-relaxed text-diem-slate">
            Photos and documents shared inside a Project stay connected to the conversation — while also
            being available together when you need them.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid lg:grid-cols-2 gap-6">
        <Reveal>
          <Card testid="media-demo">
            <div className="flex items-center gap-2.5 mb-5">
              <Image className="w-5 h-5 text-diem-blue" />
              <p className="font-display text-lg font-semibold tracking-tight text-diem-ink">Project Photos</p>
              <span className="ml-auto text-[11px] font-bold text-diem-muted bg-diem-mist rounded-full px-2.5 py-1">12</span>
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              <img className="col-span-2 row-span-2 h-full min-h-[190px] w-full object-cover rounded-2xl" alt="Home extension build" loading="lazy"
                src="https://images.unsplash.com/photo-1753893558279-8beaad18dd96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob21lJTIwZXh0ZW5zaW9uJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NzgxOTE5Nnww&ixlib=rb-4.1.0&q=85&w=800" />
              <img className="h-[90px] w-full object-cover rounded-2xl" alt="Finished living room" loading="lazy"
                src="https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwcmVub3ZhdGlvbnxlbnwwfHx8fDE3ODc4MTkxOTV8MA&ixlib=rb-4.1.0&q=85&w=400" />
              <div className="h-[90px] rounded-2xl bg-diem-ink flex items-center justify-center">
                <span className="text-white font-display font-semibold text-lg">+9</span>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {["planning-notes.pdf", "gate-drawings.pdf"].map((f) => (
                <div key={f} className="flex items-center gap-3 rounded-xl border border-diem-line bg-white px-3.5 py-2.5">
                  <FileText className="w-4 h-4 text-diem-err shrink-0" />
                  <span className="text-[13px] font-semibold text-diem-ink">{f}</span>
                  <span className="ml-auto text-[11px] text-diem-muted font-medium">Project Documents</span>
                </div>
              ))}
            </div>
          </Card>
        </Reveal>

        <Reveal delay={0.15}>
          <Card testid="search-demo">
            <p className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-diem-ink mb-6">
              Find it. <span className="text-diem-blue">Don't scroll for it.</span>
            </p>
            <div className="flex items-center gap-3 rounded-full border-2 border-diem-blue/50 bg-white px-5 py-3.5 shadow-[0_10px_30px_rgba(0,85,255,0.12)]">
              <Search className="w-5 h-5 text-diem-blue" />
              <span className="text-[15px] font-semibold text-diem-ink">4200mm</span>
              <span className="ml-auto w-[2px] h-5 bg-diem-blue animate-pulse" />
            </div>
            <div className="mt-5 rounded-2xl border border-diem-line bg-diem-paper p-4 hover:border-diem-blue/40 transition-colors duration-300 cursor-pointer" data-testid="search-result-card">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-diem-muted mb-2">
                <span className="text-diem-blue">Measurement</span> · Oakfield Drive · Project
              </div>
              <p className="text-[14px] text-diem-ink leading-snug">
                "Driveway clear opening is <mark className="bg-diem-blue/15 text-diem-blue font-bold rounded px-1">4200mm</mark>."
              </p>
              <p className="mt-2 text-[11px] text-diem-muted font-medium">James · Original message · 09:41</p>
            </div>
            <p className="mt-4 text-[12px] text-diem-muted font-medium">
              Search across conversations, projects and every message inside them.
            </p>
          </Card>
        </Reveal>
      </div>
    </div>
  </section>
);

export const OutOfHours = () => (
  <FeatureBlock
    id="out-of-hours"
    chapter="09"
    label="Out of hours"
    dark
    headline={<>Messaging without the pressure.</>}
    copy={
      <>
        <p>Work messages shouldn't make people feel permanently available.</p>
        <p className="mt-4 font-medium text-white">Send it now. They can deal with it when they're back.</p>
      </>
    }
  >
    <Card dark testid="out-of-hours-demo">
      <div className="flex items-center gap-3 pb-5 border-b border-white/10">
        <img className="w-10 h-10 rounded-full object-cover" alt="James" loading="lazy"
          src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGhlYWRzaG90fGVufDB8fHx8MTc4Nzc2OTIzOXww&ixlib=rb-4.1.0&q=85&w=96" />
        <div>
          <p className="text-sm font-bold text-white">James</p>
          <p className="text-xs text-diem-sky font-semibold flex items-center gap-1.5">
            <Moon className="w-3.5 h-3.5" /> Out of hours · back 8:00
          </p>
        </div>
        <span className="ml-auto text-[11px] font-bold text-white/40">21:47</span>
      </div>
      <div className="mt-5 space-y-2.5">
        {[
          ["Activity status hidden", true],
          ["Read receipts paused", true],
          ["Messages deliver normally", true],
        ].map(([t]) => (
          <div key={t} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3">
            <span className="w-2 h-2 rounded-full bg-diem-sky shrink-0" />
            <p className="text-sm font-semibold text-white/80">{t}</p>
          </div>
        ))}
      </div>
      <p className="mt-5 text-[12px] text-white/40 leading-relaxed">
        Out of Hours is controlled by each person — their hours, their choice.
      </p>
    </Card>
  </FeatureBlock>
);
